import { Stack } from '@mui/material';
import { PropertyTypeTagSelect, SubjectTagSelect } from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { TagSelect, TruncatedTagSelect } from '@schooly/style';
import { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../../hooks/useSchool';
import { TimetableGroupFilters } from './TimetableGroupFilterPopup';

type TimetableInlineFiltersTagsProps = {
  onOpenFilters?: () => void;
  filters: TimetableGroupFilters;
};

export const TimetableInlineFiltersTags: FC<TimetableInlineFiltersTagsProps> = ({
  filters,
  onOpenFilters,
}) => {
  const { schoolId } = useSchool();
  const { formatMessage } = useIntl();

  const tagItems = useMemo(() => {
    const items: ReactNode[] = [];

    if (!schoolId) return items;

    const commonTagsProps = {
      size: 'small',
      ...(onOpenFilters && { onClick: onOpenFilters }),
      sx: { maxWidth: 115 },
    } as const;

    if (filters.only_tutor_groups?.length) {
      items.push(
        <TagSelect
          key="tutor"
          label={formatMessage({ id: 'groups-OnlyTutorGroups' })}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filters.age_group || []) {
      items.push(
        <PropertyTypeTagSelect
          key={id}
          id={id}
          schoolId={schoolId}
          userRole={SchoolUserRole.Student}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filters.subject || []) {
      items.push(<SubjectTagSelect key={id} id={id} schoolId={schoolId} {...commonTagsProps} />);
    }

    return items;
  }, [
    schoolId,
    onOpenFilters,
    filters.only_tutor_groups,
    filters.subject,
    filters.age_group,
    formatMessage,
  ]);

  if (!tagItems.length) return null;

  return (
    <Stack flexDirection="row" alignItems="center" gap={1}>
      <TruncatedTagSelect
        spacing={2}
        items={tagItems}
        tooltipProps={{
          componentsProps: { tooltip: { sx: { maxWidth: 'unset' } } },
        }}
      />
    </Stack>
  );
};
